import type { GraphModification, ConceptDataobject, Graph } from "@lr-notitia/notitia-engineer/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-engineer/client";
import { handleGraphModification } from "./utils";
import { useSymbolsStore } from "../symbols";
import type { Terminology } from "@lr-notitia/notitia-dictionary/schemas";
import * as Metrics from "../metrics";

let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	symbolsStore = useSymbolsStore();
}

export const conceptDataobjects = (graph: Ref<Graph>) =>
	computed<ConceptDataobject[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "dataobject" && node.layer === "concept"
			) as ConceptDataobject[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase()
				)
		);
	});
export function conceptDataobject(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => {
			Metrics.recordAccess(id);
			return graph.value.nodes[id] as ConceptDataobject | undefined;
		},
		add: (data: { refersTo: string; localTerminology: Terminology }) => add(domainId, graph, data),
		update: (dataobject: ConceptDataobject) => update(domainId, graph, dataobject),
		remove: (id: string) => remove(domainId, graph, id),
	};
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	data: { refersTo: string; localTerminology: Terminology }
): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		api()
			.domain(domainId.value)
			.dataobjects()
			.post(data)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(domainId: Ref<string>, graph: Ref<Graph>, dataobject: ConceptDataobject): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[dataobject.id]) return reject();
		api()
			.domain(domainId.value)
			.dataobject(dataobject.id)
			.put(dataobject)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.dataobject(id)
			.del()
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
